// src/utils/pdfGenerator.js — Professional Laboratory COA Design
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { getVerifyUrl, SITE_URL } from '../config/site';

const W      = 210;
const H      = 297;
const MARGIN = 14;
const FOOTER_Y = 282;
const SAFE_Y   = 274;

// ── Colours ──────────────────────────────────────────────────────────────────
const NAVY    = [15,  40, 100];
const BLUE    = [30,  80, 160];
const ACCENT  = [0,  100, 180];
const DGRAY   = [60,  60,  70];
const MGRAY   = [120,120,130];
const LGRAY   = [230,232,238];
const WHITE   = [255,255,255];
const BLACK   = [20,  20,  25];
const GREEN   = [22, 163,  74];
const RED     = [220, 38,  38];

// Character sanitizer for jsPDF — Helvetica doesn't support Unicode
const sanitizeText = (str) => {
  if (!str) return str;
  return String(str)
    .replace(/≥/g, '>=')   // ≥
    .replace(/≤/g, '<=')   // ≤
    .replace(/µ/g, 'u')    // µ
    .replace(/°/g, 'deg')  // °
    .replace(/±/g, '+/-')  // ±
    .replace(/α/g, 'alpha').replace(/β/g, 'beta').replace(/γ/g, 'gamma')
    .replace(/²/g, '^2').replace(/³/g, '^3')
    .replace(/–/g, '-').replace(/—/g, '--')
    .replace(/‘/g, "'").replace(/’/g, "'")
    .replace(/“/g, '"').replace(/”/g, '"');
};


// Normalize stored values to display-friendly labels
const DISPLAY_LABELS = {
  // Line
  human: 'Research Use Only', veterinary: 'Veterinary Use', agricultural: 'Agricultural Use',
  humana: 'Research Use Only', veterinaria: 'Veterinary Use', agricola: 'Agricultural Use',
  // Category
  medication: 'Research Compound', cosmetic: 'Cosmetic', supplement: 'Dietary Supplement',
  phytotherapy: 'Phytotherapy', homeopathic: 'Homeopathic', other: 'Research Compound',
  medicamento: 'Research Compound', cosmetico: 'Cosmetic', suplemento: 'Dietary Supplement',
  fitoterapeutico: 'Phytotherapy', homeopatico: 'Homeopathic', otro: 'Research Compound',
  // Storage
  ambient: 'Ambient 25±5°C', refrigeration: 'Refrigerated 2-8°C', 'light-sensitive': 'Light-sensitive / Protect from light',
  ambiente: 'Ambient 25±5°C', refrigeracion: 'Refrigerated 2-8°C', fotosensible: 'Light-sensitive / Protect from light',
};
const displayLabel = (val) => val ? (DISPLAY_LABELS[val.toLowerCase()] || val) : val;

export const generatePDF = async (data, lang = 'en') => {
  const {
    certId, includeQR, includeLogo, logoDataUrl, productLabelUrl,
    doctorName, doctorTitle, doctorSignatureUrl,
    results, charts, richText, _spacing,
    contact, sample, analysis,
    product, specs, lab,
  } = data;

  const isNew = !!sample?.productName;
  const S = sanitizeText; // shorthand

  // ── Normalise — all user values sanitized at source ───────────────────────
  const productName  = S(isNew ? (sample?.productName        ||'—') : (product?.chemName   ||'—'));
  const batchLot     = S(isNew ? (sample?.lot                ||'—') : (product?.batch      ||'—'));
  const casNumber    = S(isNew ? (sample?.activeIngredient   ||'—') : (product?.casNumber  ||'—'));
  const formula      = S(isNew ? (sample?.pharmaceuticalForm ||'—') : (product?.formula    ||'—'));
  const weight       = S(isNew ? (sample?.doseConcentration  ||'—') : (product?.weight     ||'—'));
  const mfgDate      = S(isNew ? (sample?.mfgDate            ||'—') : (product?.mfgDate    ||'—'));
  const expDate      = S(isNew ? (sample?.expDate            ||'—') : (product?.expDate    ||'—'));
  const appearance   = S(isNew ? (sample?.presentation       ||'' ) : (product?.appearance ||''));
  const storageVal   = displayLabel(S(isNew ? (sample?.storageCondition   ||'' ) : (product?.storage    ||'')));
  const manufacturer = S(isNew ? (sample?.manufacturer       ||'' ) : (product?.country    ||''));
  const companyName  = S(isNew ? (contact?.companyName       ||'' ) : '');
  const labName      = S(isNew ? (sample?.supplier           ||'' ) : (lab?.labName        ||''));
  const labAddress   = S(isNew ? (contact?.address           ||'' ) : (lab?.address        ||''));
  const samplingDate = S(isNew ? (sample?.samplingDate       ||'' ) : (lab?.approvalDate   ||''));
  const sampleType   = S(isNew && Array.isArray(sample?.sampleType)  ? sample.sampleType.join(', ')  : '');
  const methodology  = S(isNew && Array.isArray(sample?.methodology) ? sample.methodology.join(', ') : '');
  const productLine  = displayLabel(S(isNew ? (sample?.line     ||'') : ''));
  const category     = displayLabel(S(isNew ? (sample?.category ||'') : ''));

  // ── Analysis labels ───────────────────────────────────────────────────────
  const ANLBL = {
    en:{aspecto:'Appearance',ph:'pH',conductividad:'Conductivity',toc:'TOC',nitritos:'Nitrites',nitratos:'Nitrates',color:'Color',turbiedad:'Turbidity',cloroLibre:'Free Residual Chlorine',durezaTotal:'Total Hardness',sulfatos:'Sulfates',alcalinidad:'Alkalinity/Acidity',metalesPesados:'Heavy Metals',densidad:'Density',kFisher:'Karl Fischer (Water)',solubilidad:'Solubility',puntoDeFusion:'Melting Point',viscosidad:'Viscosity',perdidaPorSecado:'Loss on Drying',indiceRefraccion:'Refractive Index',residuoIgnicion:'Ignition Residue',rotacionOptica:'Optical Rotation',indiceYodo:'Iodine Value',indiceSaponificacion:'Saponification Value',acidez:'Acidity',materiaAnionica:'Anionic Matter',friabilidad:'Friability',dureza:'Hardness',desintegracion:'Disintegration',disolucion:'Dissolution',pesoPromedio:'Average Weight',volumetria:'Volumetry',valoracionUV:'UV Assay',valoracionHPLC:'HPLC Assay',otros:'Other'},
    de:{aspecto:'Aussehen',ph:'pH',conductividad:'Leitfähigkeit',toc:'TOC',nitritos:'Nitrite',nitratos:'Nitrate',color:'Farbe',turbiedad:'Trübung',cloroLibre:'Freies Restchlor',durezaTotal:'Gesamthärte',sulfatos:'Sulfate',alcalinidad:'Alkalinität',metalesPesados:'Schwermetalle',densidad:'Dichte',kFisher:'Karl Fischer',solubilidad:'Löslichkeit',puntoDeFusion:'Schmelzpunkt',viscosidad:'Viskosität',perdidaPorSecado:'Verlust',indiceRefraccion:'Brechungsindex',residuoIgnicion:'Glührückstand',rotacionOptica:'Opt. Drehung',indiceYodo:'Jodzahl',indiceSaponificacion:'Verseifungszahl',acidez:'Säuregehalt',materiaAnionica:'Anion. Materie',friabilidad:'Friabilität',dureza:'Härte',desintegracion:'Zerfall',disolucion:'Auflösung',pesoPromedio:'Durchschnittsgewicht',volumetria:'Volumetrie',valoracionUV:'UV-Valorisierung',valoracionHPLC:'HPLC-Valorisierung',otros:'Andere'},
  };
  const LBL = ANLBL[lang]||ANLBL.en;

  // ── Specs rows ────────────────────────────────────────────────────────────
  let specsRows = [];
  if (isNew && analysis) {
    Object.entries(analysis).forEach(([k,v]) => {
      if (k==='observations'||!v?.selected) return;
      const pr  = results?.paramResults?.[k];
      const res = pr?.result||'—';
      const pass= pr?.pass==='fail'?'FAIL':(pr?.pass==='pass'||pr?.result)?'PASS':'—';
      specsRows.push([sanitizeText(LBL[k]||k), sanitizeText(v.spec||'—'), sanitizeText(res), pass]);
    });
  } else {
    specsRows = (specs||[]).map(r=>[sanitizeText(r.parameter||''),sanitizeText(r.specification||''),sanitizeText(r.result||''),sanitizeText(r.method||'')]);
  }

  // ── PDF init ──────────────────────────────────────────────────────────────
  const doc = new jsPDF({orientation:'portrait',unit:'mm',format:'a4'});
  const sp = (key) => (_spacing?.[key] || 0);

  const set = (font, size, color) => {
    doc.setFont('helvetica', font);
    doc.setFontSize(size);
    doc.setTextColor(...(color || BLACK));
  };

  const addWatermark = () => {
    set('normal',10,LGRAY);
    for (let py=10; py<=H+30; py+=30)
      for (let px=-30; px<=W+50; px+=52)
        doc.text('coalab.us', px, py, {angle:42});
    doc.setTextColor(...BLACK);
  };

  const drawHR = (y,color,thick=0.4) => {
    doc.setDrawColor(...(color||LGRAY)); doc.setLineWidth(thick);
    doc.line(MARGIN, y, W-MARGIN, y);
  };

  const addPageHeader = (rightText) => {
    // Watermark FIRST so bars render on top
    addWatermark();
    // Thin top bar
    doc.setFillColor(...NAVY); doc.rect(0,0,W,6,'F');
    // Logo area
    set('bold',14,NAVY); doc.text('COA',MARGIN,18);
    set('bold',8,ACCENT); doc.text('Lab',MARGIN+12,18);
    set('normal',7,MGRAY); doc.text(SITE_URL.replace('https://',''), MARGIN, 22);
    // Right info
    set('normal',7,MGRAY); doc.text(rightText||'', W-MARGIN, 12, {align:'right'});
    set('bold',7,NAVY); doc.text(certId, W-MARGIN, 17, {align:'right'});
    // Divider
    drawHR(25, LGRAY, 0.5);
  };

  const addFooter = () => {
    drawHR(FOOTER_Y, LGRAY, 0.3);
    set('normal',7,MGRAY);
    doc.text(SITE_URL.replace('https://',''), MARGIN, FOOTER_Y+5);
    doc.text(certId, W/2, FOOTER_Y+5, {align:'center'});
    doc.text(new Date().toLocaleDateString(), W-MARGIN, FOOTER_Y+5, {align:'right'});
  };

  const checkY = (y, need) => {
    if (y+need > SAFE_Y) {
      addFooter(); doc.addPage(); addPageHeader(lang==='de'?'Analysenzertifikat':'Certificate of Analysis'); return 30;
    }
    return y;
  };

  const loadImage = async (url, asPng=false) => {
    return new Promise(resolve => {
      const img = new Image(); img.crossOrigin='anonymous';
      img.onload = () => {
        const c=document.createElement('canvas');
        const r=Math.min(900/img.width,900/img.height,1);
        c.width=img.width*r; c.height=img.height*r;
        const ctx=c.getContext('2d');
        if (!asPng){ctx.fillStyle='#fff';ctx.fillRect(0,0,c.width,c.height);}
        ctx.drawImage(img,0,0,c.width,c.height);
        resolve({base64:c.toDataURL(asPng?'image/png':'image/jpeg',0.92),w:img.naturalWidth,h:img.naturalHeight});
      };
      img.onerror=async()=>{
        try{
          const res=await fetch(url); const blob=await res.blob();
          const b64=await new Promise((r2,j2)=>{const rd=new FileReader();rd.onload=()=>r2(rd.result);rd.onerror=j2;rd.readAsDataURL(blob);});
          const t=new Image(); await new Promise(r3=>{t.onload=r3;t.src=b64;});
          resolve({base64:b64,w:t.naturalWidth||400,h:t.naturalHeight||300});
        }catch{resolve(null);}
      };
      img.src=url;
    });
  };

  // ── PAGE 1 ────────────────────────────────────────────────────────────────
  // Watermark FIRST so header bars render on top of it
  addWatermark();
  // Top bar (drawn after watermark so it covers it)
  doc.setFillColor(...NAVY); doc.rect(0,0,W,6,'F');

  // Logo / lab name (left)
  set('bold',18,NAVY); doc.text('COA',MARGIN,20);
  set('bold',9,[0,100,180]); doc.text('Lab',MARGIN+16,20);
  set('normal',7,MGRAY); doc.text(SITE_URL.replace('https://',''),MARGIN,25);

  // Right block: IDs and dates
  const today = new Date().toLocaleDateString();
  set('normal',8,MGRAY);
  doc.text(lang==='de'?'Berichtsnummer:':'Report Number:',  W-MARGIN,13,{align:'right'});
  doc.text(lang==='de'?'Ausgestellt:':'Date Issued:', W-MARGIN,19,{align:'right'});
  set('bold',8,NAVY);
  doc.text(certId, W-MARGIN,17,{align:'right'});
  doc.text(today,  W-MARGIN,23,{align:'right'});

  // Company (if any)
  if (companyName) {
    set('normal',8,DGRAY);
    doc.text(companyName, MARGIN, 30);
    if (labAddress) doc.text(labAddress, MARGIN, 34);
  }

  // Main title
  drawHR(38, LGRAY, 0.5);
  set('bold',20,NAVY);
  const titleText = lang==='de'?'Analysenzertifikat':'Certificate of Analysis';
  doc.text(titleText, W/2, 47, {align:'center'});
  set('normal',9,MGRAY);
  doc.text(productName, W/2, 53, {align:'center'});
  drawHR(57, LGRAY, 0.5);

  // QR
  let qrDataUrl=null;
  if (includeQR) {
    try { qrDataUrl=await QRCode.toDataURL(getVerifyUrl(certId),{width:160,margin:1,color:{dark:'#0f2864',light:'#ffffff'}}); }
    catch{}
  }
  const qrS=24, qrX=W-MARGIN-qrS, qrY=60;
  if (qrDataUrl) {
    doc.setDrawColor(...LGRAY); doc.setLineWidth(0.3);
    doc.rect(qrX-1,qrY-1,qrS+2,qrS+2);
    doc.addImage(qrDataUrl,'PNG',qrX,qrY,qrS,qrS);
    set('normal',6,MGRAY); doc.text(lang==='de'?'Scannen zum Prüfen':'Scan to verify',qrX+qrS/2,qrY+qrS+4,{align:'center'});
  }

  // ── Sample info table (left of QR) ───────────────────────────────────────
  let y = 62;
  const infoW = qrDataUrl ? qrX - MARGIN - 4 : W - MARGIN*2;
  const col1  = MARGIN;
  const col2  = col1 + 36;
  const col3  = col1 + infoW/2;
  const col4  = col3 + 36;

  const infoRows = [
    [lang==='de'?'Verbindung:':'Compound:',      productName,
     lang==='de'?'Charge/Lot:':'Batch/Lot:',     batchLot],
    [lang==='de'?'Wirkstoff:':'Active Ingr.:',   casNumber,
     lang==='de'?'Herstell.:':'Mfg. Date:',      mfgDate],
    [lang==='de'?'Form:':'Form:',                formula,
     lang==='de'?'Ablauf:':'Exp. Date:',          expDate],
    [lang==='de'?'Dosis:':'Dose/Weight:',        weight,
     lang==='de'?'Herstellungsland:':'Country of Manufacture:', manufacturer||companyName],
  ];

  infoRows.forEach(([l1,v1,l2,v2],i) => {
    const ry = y + i*7;
    // subtle alternating row
    if (i%2===0) { doc.setFillColor(246,248,252); doc.rect(col1,ry-4,infoW,7,'F'); }
    set('bold',7.5,MGRAY);   doc.text(l1,col1+1,ry);
    set('normal',7.5,BLACK); doc.text(String(v1),col2,ry,{maxWidth:infoW/2-40});
    set('bold',7.5,MGRAY);   doc.text(l2,col3+1,ry);
    set('normal',7.5,BLACK); doc.text(String(v2),col4,ry,{maxWidth:infoW/2-40});
  });

  y += 32;

  // Extra sample fields
  const extras = [
    [lang==='de'?'Probentyp:':'Sample Type:', sampleType],
    [lang==='de'?'Methodik:':'Methodology:',  methodology],
    [lang==='de'?'Linie:':'Line:',            productLine],
    [lang==='de'?'Kategorie:':'Category:',    category],
    [lang==='de'?'Darr.-form:':'Presentation:',appearance],
    [lang==='de'?'Lagerung:':'Storage:',      storageVal],
  ].filter(([,v])=>v);

  if (extras.length) {
    extras.forEach(([l,v],i) => {
      const ry = y + i*6.5;
      if (i%2===0) { doc.setFillColor(246,248,252); doc.rect(MARGIN,ry-4,W-MARGIN*2,6.5,'F'); }
      set('bold',7,MGRAY);   doc.text(l, col1+1, ry);
      set('normal',7,BLACK); doc.text(String(v), col2, ry, {maxWidth:W-MARGIN*2-40});
    });
    y += extras.length*6.5 + 4;
  }

  // ── Label image + Specs table ─────────────────────────────────────────────
  y += 2; drawHR(y, LGRAY); y += 6;

  let labelBase64=null, labelPxW=0, labelPxH=0;
  if (productLabelUrl) {
    try { const r=await loadImage(productLabelUrl); if(r){labelBase64=r.base64;labelPxW=r.w;labelPxH=r.h;} }catch{}
  }

  const hasLabel   = !!labelBase64;
  const labelColW  = hasLabel ? 48 : 0;
  const labelGap   = hasLabel ? 5  : 0;
  const specsLeft  = MARGIN + labelColW + labelGap;
  const specsWidth = W - specsLeft - MARGIN;
  const specsY     = y;

  // Specs header
  set('bold',9,NAVY);
  doc.text(lang==='de'?'Analyseergebnisse':'Analysis Results', specsLeft, y);
  y += 5;

  const tHead = lang==='de'
    ? [['Parameter','Spezifikation','Ergebnis','Bewertung']]
    : [['Parameter','Specification','Result','Assessment']];

  doc.autoTable({
    startY: y,
    head: tHead,
    body: specsRows.length ? specsRows : [['—','—','—','—']],
    styles:             {fontSize:8, cellPadding:3},
    headStyles:         {fillColor:NAVY, textColor:255, fontStyle:'bold', fontSize:7.5, cellPadding:3},
    alternateRowStyles: {fillColor:[248,249,252]},
    columnStyles:       {2:{fontStyle:'bold'}, 3:{textColor:WHITE, fontStyle:'bold'}},
    margin:             {left:specsLeft, right:MARGIN},
    tableWidth:         specsWidth,
    didDrawCell: (d) => {
      if (d.section==='body'&&d.column.index===3) {
        const v=String(d.cell.raw||'');
        if (v==='PASS'||v==='FAIL') {
          const bg=v==='PASS'?GREEN:RED;
          doc.setFillColor(...bg);
          doc.rect(d.cell.x, d.cell.y, d.cell.width, d.cell.height,'F');
          set('bold',7.5,WHITE);
          doc.text(v, d.cell.x+d.cell.width/2, d.cell.y+d.cell.height/2+2.5,{align:'center'});
        }
      }
    },
  });

  const specsEndY = doc.lastAutoTable.finalY;

  // Label image aligned with table
  if (hasLabel) {
    const availH  = Math.max(specsEndY - specsY - 5, 28);
    const pxRatio = labelPxW/labelPxH;
    let dW=labelColW, dH=dW/pxRatio;
    if (dH>availH){dH=availH;dW=dH*pxRatio;}
    if (dW>labelColW){dW=labelColW;dH=dW/pxRatio;}
    const iX=MARGIN+(labelColW-dW)/2, iY=specsY+5+(availH-dH)/2;
    // Label frame
    doc.setFillColor(248,249,252); doc.setDrawColor(...LGRAY); doc.setLineWidth(0.3);
    doc.rect(MARGIN, specsY+5, labelColW, availH);
    doc.addImage(labelBase64,'JPEG',iX,iY,dW,dH,undefined,'FAST');
    set('bold',7,NAVY); doc.text(lang==='de'?'Etikett':'Label',MARGIN,specsY-1);
  }

  y = specsEndY + 6 + sp('afterSpecs');

  // Observations
  if (isNew && analysis?.observations && typeof analysis.observations==='string' && analysis.observations.trim()) {
    y = checkY(y,10);
    set('bold',7.5,MGRAY); doc.text(lang==='de'?'Beobachtungen:':'Observations:', MARGIN, y);
    set('normal',7.5,DGRAY);
    const obs=doc.splitTextToSize(S(analysis.observations), W-MARGIN*2-45);
    doc.text(obs, MARGIN+45, y); y+=obs.length*4.5+3;
  }

  // ── Results highlight ─────────────────────────────────────────────────────
  if (results?.purity) {
    y = checkY(y, 30);
    y += 2; drawHR(y,LGRAY); y += 6;
    set('bold',8.5,NAVY); doc.text(lang==='de'?'Testergebnisse':'Test Results', MARGIN, y); y += 6;

    const metrics=[
      [lang==='de'?'Reinheit':'Purity',         results.purity+(results.purity.includes('%')?'':'%')],
      [lang==='de'?'Gesamtgehalt':'Total Content', results.totalContent||'—'],
      [lang==='de'?'Erwartet':'Expected',        results.expectedContent||'—'],
      [lang==='de'?'Füllgenauigkeit':'Fill Accuracy', results.fillAccuracy?(results.fillAccuracy+(results.fillAccuracy.includes('%')?'':'%')):'—'],
    ];
    const cW=(W-MARGIN*2)/metrics.length;
    metrics.forEach(([lbl,val],i)=>{
      const cx=MARGIN+cW*i;
      // box
      doc.setFillColor(...LGRAY); doc.setDrawColor(...LGRAY); doc.rect(cx,y-2,cW-1,16,'F');
      set('bold',6.5,NAVY);   doc.text(lbl,  cx+cW/2, y+4, {align:'center'});
      set('bold',13,ACCENT);  doc.text(String(val), cx+cW/2, y+13, {align:'center'});
    });
    y += 20;

    if (results.appearance) {
      y=checkY(y,7); set('bold',7.5,MGRAY);
      doc.text(lang==='de'?'Aussehen:':'Appearance:', MARGIN,y);
      set('normal',7.5,BLACK); doc.text(S(results.appearance), MARGIN+28,y,{maxWidth:W-MARGIN*2-30}); y+=6;
    }
    if (results.notes) {
      y=checkY(y,8); set('italic',7.5,MGRAY);
      const nl=doc.splitTextToSize(S(results.notes),W-MARGIN*2);
      nl.forEach(l=>{y=checkY(y,5);doc.text(l,MARGIN,y);y+=5;});
    }
    y += 2 + sp('afterResults');
  }

  // ── PASS + certification statement ────────────────────────────────────────
  y=checkY(y,20); y+=4; drawHR(y,LGRAY); y+=6;

  // PASS/FAIL stamp style
  doc.setFillColor(...GREEN); doc.roundedRect(MARGIN,y,32,10,1,1,'F');
  set('bold',9,WHITE); doc.text(lang==='de'?'BESTANDEN':'PASS', MARGIN+16,y+6.8,{align:'center'});
  set('normal',7.5,DGRAY);
  const stmt=lang==='de'
    ? 'Die Probe wurde geprüft und entspricht den aufgeführten Spezifikationen.'
    : 'The sample has been tested and found to comply with the specifications listed herein.';
  const stmtLines=doc.splitTextToSize(stmt,W-MARGIN*2-38);
  doc.text(stmtLines, MARGIN+36, y+4); y+=16;

  // ── Contact / Lab info ────────────────────────────────────────────────────
  if (companyName||labName||labAddress||samplingDate) {
    y=checkY(y,22);
    set('bold',7.5,NAVY); doc.text(lang==='de'?'Kontaktinformationen':'Contact Information',MARGIN,y); y+=4;
    drawHR(y,LGRAY); y+=5;
    const cInfo=[
      [lang==='de'?'Auftraggeber:':'Client:',    companyName||labName],
      [lang==='de'?'Adresse:':'Address:',        labAddress],
      [lang==='de'?'Entnahmedatum:':'Sampling:', samplingDate],
    ].filter(([,v])=>v);
    cInfo.forEach(([l,v])=>{
      y=checkY(y,6);
      set('bold',7.5,MGRAY); doc.text(l,MARGIN,y);
      set('normal',7.5,BLACK); doc.text(S(String(v)),MARGIN+30,y,{maxWidth:W-MARGIN*2-32});
      y+=6;
    });
    y += 2 + sp('afterContact');
  }

  // ── Charts — flow naturally, new page only when needed ──────────────────
  const chartsToRender = (charts||[]).filter(c=>c?.url);
  if (chartsToRender.length > 0) {
    y = checkY(y, 30); // section header needs ~30mm
    // Charts section header
    set('bold',9,NAVY); doc.text(lang==='de'?'Analysediagramme':'Analysis Charts', MARGIN, y);
    drawHR(y+3, LGRAY); y += 8;

    for (const ch of chartsToRender) {
      try {
        const r = await loadImage(ch.url);
        if (!r?.base64) continue;
        const cgW = W - MARGIN*2;
        const cgH = cgW * (200/560);
        const need = (ch.title ? 9 : 0) + cgH + 10;
        y = checkY(y, need);
        if (ch.title) { set('bold',9,ACCENT); doc.text(ch.title, MARGIN, y); y += 6; }
        doc.setDrawColor(...LGRAY); doc.setLineWidth(0.3);
        doc.rect(MARGIN, y, cgW, cgH);
        doc.addImage(r.base64,'PNG', MARGIN, y, cgW, cgH, undefined,'FAST');
        y += cgH + sp('afterCharts') + 10;
      } catch(e) { console.warn(e); }
    }
    y += 4;
  }

  // ── Methods / Notes — flow naturally ──────────────────────────────────────
  const getPlainText = (html) => { if(!html)return''; const t=document.createElement('div'); t.innerHTML=html; return t.innerText?.trim()||''; };
  const hasSec = (richText?.sections||[]).filter(s=>s.key!=='custom'&&s.text?.trim()).length > 0;
  const hasFree = getPlainText(richText?.html).length > 10;
  const hasRich = hasSec || hasFree;

  if (hasRich) {
    y = checkY(y, 30); // section header
    set('bold',9,NAVY); doc.text(lang==='de'?'Methoden & Hinweise':'Methods Summary', MARGIN, y);
    drawHR(y+3, LGRAY); y += 8;

    for (const sec of (richText?.sections||[])) {
      if (sec.key==='custom'||!sec.text?.trim()) continue;
      y = checkY(y, 22);
      set('bold',9.5,NAVY); doc.text(sec.label, MARGIN, y);
      if (sec.standard) { set('bold',8.5,ACCENT); doc.text(sec.standard, W-MARGIN, y, {align:'right'}); }
      y += 4; drawHR(y,[180,195,215],0.3); y += 5;
      set('normal',8,DGRAY);
      sec.text.split('\n').forEach(raw => {
        const t = raw.trim(); if (!t) return;
        const ls = doc.splitTextToSize('• '+sanitizeText(t), W-MARGIN*2-6);
        ls.forEach(l => { y=checkY(y,5); doc.text(l, MARGIN+4, y); y+=5; });
      });
      y += 8;
    }

    if (richText?.html?.trim()) {
      const div = document.createElement('div'); div.innerHTML = richText.html;
      const parse = (node) => {
        if (node.nodeType===Node.TEXT_NODE) {
          const t=node.textContent.replace(/\n/g,' ').trim(); if(!t)return;
          set('normal',8,DGRAY);
          const ls=doc.splitTextToSize(sanitizeText(t),W-MARGIN*2);
          ls.forEach(l=>{y=checkY(y,5);doc.text(l,MARGIN,y);y+=5;}); return;
        }
        if (node.nodeType!==Node.ELEMENT_NODE) return;
        const tag=node.tagName?.toLowerCase();
        if (tag==='div'&&node.style?.marginBottom==='20px') return;
        if (['h1','h2','h3'].includes(tag)) {
          y+=3; y=checkY(y,10);
          set('bold',tag==='h1'?11:9.5,NAVY);
          const ls=doc.splitTextToSize(sanitizeText(node.innerText||node.textContent),W-MARGIN*2);
          ls.forEach(l=>{y=checkY(y,6);doc.text(l,MARGIN,y);y+=6;}); y+=2; return;
        }
        if (['b','strong'].includes(tag)) {
          set('bold',8,BLACK);
          const ls=doc.splitTextToSize(sanitizeText(node.textContent.trim()),W-MARGIN*2);
          ls.forEach(l=>{y=checkY(y,5);doc.text(l,MARGIN,y);y+=5;}); return;
        }
        if (tag==='li') {
          set('normal',8,DGRAY);
          const ls=doc.splitTextToSize(sanitizeText('• '+(node.innerText||node.textContent).trim()),W-MARGIN*2-6);
          ls.forEach(l=>{y=checkY(y,5);doc.text(l,MARGIN+4,y);y+=5;}); return;
        }
        if (['div','p'].includes(tag)) { node.childNodes.forEach(parse); y+=2; return; }
        node.childNodes.forEach(parse);
      };
      y += 4; div.childNodes.forEach(parse);
    }
    y += sp('afterMethods') + 4;
  }

  // ── Doctor signature — flows naturally after previous content ─────────────
  if (doctorName) {
    const sigH   = 20;
    const sigNeeded = sigH + 28 + sp('beforeDoctor'); // line+name+title+reported
    y = checkY(y, sigNeeded);
    y += sp('beforeDoctor'); // apply doctor offset (negative = less space before)

    drawHR(y, LGRAY); y += 6;

    // Load signature
    let sigB64 = null;
    if (doctorSignatureUrl) {
      try { const r=await loadImage(doctorSignatureUrl,true); if(r?.base64) sigB64=r.base64; } catch{}
    }

    const sigW = 65;
    if (sigB64) {
      const pxW=100,pxH=40,sR=pxW/pxH;
      let dW=sigW,dH=dW/sR;
      if (dH>sigH){dH=sigH;dW=dH*sR;}
      const sX=MARGIN+(sigW-dW)/2, sY=y+(sigH-dH)/2;
      doc.addImage(sigB64,'PNG',sX,sY,dW,dH,undefined,'FAST');
    }
    doc.setDrawColor(...DGRAY); doc.setLineWidth(0.4);
    doc.line(MARGIN, y+sigH, MARGIN+sigW, y+sigH);
    y += sigH + 4;
    set('bold',8.5,BLACK); doc.text(S(doctorName), MARGIN, y); y+=5;
    if (doctorTitle){set('normal',7.5,MGRAY);doc.text(S(doctorTitle),MARGIN,y);y+=5;}
    set('italic',7,MGRAY);
    doc.text((lang==='de'?'Gezeichnet von: ':'Reported by: ')+doctorName+' | '+today, MARGIN, y+2);
    y += 8;
  }

  addFooter(); // single footer call at the very end

  // If _returnBlob flag is set, return blob for live preview instead of saving
  if (data._returnBlob) {
    return doc.output('blob');
  }
  doc.save(certId + '.pdf');
};
